import { styles } from "@/reports/reportStyles";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { useInspectionStore } from "@/stores/inspection";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { getIN15Value, getIN15ValueFromMaterials, areThereNonControllingIN15Changes } from "@/composables/keyFields.js";

function setRatingsDocDefinition(structure) {
  const configStore = useConfigStore();
  const inspectionStore = useInspectionStore();
  const inspectionFields = structure?.keyFields;
  let seismicVulnerability = getSeismicVulnerability(inspectionStore, structure?.userBridge?.seismicVuln);

  let nstmInspectionCondition = getCondition(inspectionStore, structure?.userInspection?.nstmCondition);
  let docDefinition = {
    header: function () {
      return getReportHeader("Ratings Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Ratings Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Inspection Status",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "20%",
            text: "IE.34 Inspection Status: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.INSPECTION_STATUS,
              structure?.inspection?.inspectionStatus,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Key Field Comparison since Last Accepted Inspection",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "29%",
            text: "",
            margin: [0, 5, 0, 0],
          },
          {
            width: "7%",
            text: "Current",
            bold: true,
            alignment: "center",
            margin: [0, 3, 0, 0],
          },
          {
            width: "4%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "9%",
            text: "Previous",
            bold: true,
            alignment: "center",
            margin: [0, 3, 0, 0],
          },
          {
            width: "16%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "12%",
            text: "IE.01 Required",
            bold: true,
            alignment: "center",
            margin: [0, 3, 0, 0],
          },
          {
            width: "8%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "12%",
            text: "IE.03 Interval",
            bold: true,
            alignment: "center",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          heights: 14,
          widths: ["49%", "0.5%", "11.5%", "19%", "0.5%", "19%", "3.5%"],
          body: [
            [
              getRatingsTable(inspectionStore, structure, getFormattedDateStringNoTime, inspectionFields),
              {
                text: "",
                margin: [0, 0, 0, 0],
              },
              {
                table: {
                  headerRows: 0,
                  heights: 14,
                  widths: ["100%"],
                  body: [
                    [
                      {
                        text: "\n",
                      },
                    ],
                    [
                      {
                        text:
                          "2 - Routine" +
                          getCheckboxChangedAsterisk(
                            "N/A",
                            "N/A",
                            inspectionFields?.snbiFields?.rouInterval,
                            inspectionFields?.previousSnbiFields?.rouInterval,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "3 - Underwater" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.uwRequired,
                            inspectionFields?.previousSnbiFields?.uwRequired,
                            inspectionFields?.snbiFields?.uwInterval,
                            inspectionFields?.previousSnbiFields?.uwInterval,
                          ),
                        noWrap: true,
                        bold: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "4 - NSTM" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.nstmRequired,
                            inspectionFields?.previousSnbiFields?.nstmRequired,
                            inspectionFields?.snbiFields?.nstmInterval,
                            inspectionFields?.previousSnbiFields?.nstmInterval,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "6 - In-Depth" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.indRequired,
                            inspectionFields?.previousSnbiFields?.indRequired,
                            inspectionFields?.snbiFields?.indRequired,
                            inspectionFields?.previousSnbiFields?.indRequired,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "7 - Special" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.osRequired,
                            inspectionFields?.previousSnbiFields?.osRequired,
                            inspectionFields?.snbiFields?.osRequired,
                            inspectionFields?.previousSnbiFields?.osRequired,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "8 - Service" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.servRequired,
                            inspectionFields?.previousSnbiFields?.servRequired,
                            inspectionFields?.snbiFields?.servRequired,
                            inspectionFields?.previousSnbiFields?.servRequired,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                    [
                      {
                        text:
                          "E - Elements" +
                          getCheckboxChangedAsterisk(
                            inspectionFields?.snbiFields?.elemRequired,
                            inspectionFields?.previousSnbiFields?.elemRequired,
                            inspectionFields?.snbiFields?.elemRequired,
                            inspectionFields?.previousSnbiFields?.elemRequired,
                          ),
                        bold: true,
                        noWrap: true,
                        alignment: "center",
                        margin: [0, 1.5, 0, 0],
                      },
                    ],
                  ],
                },
                layout: "noBorders",
                margin: [0, 0, 0, 0],
              },
              {
                table: {
                  heights: 14,
                  headerRows: 0,
                  widths: ["50%", "50%"],
                  body: [
                    [
                      {
                        text: "Current",
                        alignment: "center",
                      },
                      {
                        text: "Previous",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text: "N/A",
                        alignment: "center",
                        margin: [0, 0, 0, 0],
                      },
                      {
                        text: "N/A",
                        alignment: "center",
                        margin: [0, 0, 0, 0],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.uwRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.previousSnbiFields?.uwRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.nstmRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.previousSnbiFields?.nstmRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.indRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.previousSnbiFields?.indRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.osRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.previousSnbiFields?.osRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 1],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.servRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.previousSnbiFields?.servRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 1],
                      },
                    ],
                    [
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.elemRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 0],
                      },
                      {
                        text: inspectionStore.getReportCheckbox(inspectionFields?.snbiFields?.elemRequired),
                        alignment: "center",
                        style: "customFontAwesome",
                        margin: [0, 1.75, 0, 1],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: function (i) {
                    return i < 2 || i > 7 ? 1 : 0;
                  },
                },
                margin: [0, 0, 0, 0],
              },

              {
                text: "",
                margin: [0, 0, 0, 0],
              },
              {
                table: {
                  heights: 14,
                  headerRows: 0,
                  widths: ["50%", "50%"],
                  body: [
                    [
                      {
                        text: "Current",
                        alignment: "center",
                      },
                      {
                        text: "Previous",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.rouInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.rouInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.rouInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.rouInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.uwInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.uwInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.uwInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.uwInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.nstmInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.nstmInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.nstmInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.nstmInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.indInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.indInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.indInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.indInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.osInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.osInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.osInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.osInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.servInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.servInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.servInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.servInterval + " mos",
                        alignment: "center",
                      },
                    ],
                    [
                      {
                        text:
                          inspectionFields?.snbiFields?.elemInterval == null
                            ? "--"
                            : inspectionFields?.snbiFields?.elemInterval + " mos",
                        alignment: "center",
                      },
                      {
                        text:
                          inspectionFields?.previousSnbiFields?.elemInterval == null
                            ? "--"
                            : inspectionFields?.previousSnbiFields?.elemInterval + " mos",
                        alignment: "center",
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: function (i) {
                    return i < 2 || i > 7 ? 1 : 0;
                  },
                },
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 0, 0, 0],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Condition Ratings",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.01 Deck: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.dkRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.09 Channel: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.chanRating),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.02 Superstructure: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.supRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.10 Channel Protection: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.chanProtRating,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.03 Substructure: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.subRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.11 Scour: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_CONDITION_RATING,
              inspectionStore.getOverallScourHole(structure.userInspection.scourCondRating, null, structure),
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.04 Culvert: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.culvRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.13 Lowest Condition Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.lowestCondition,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.07 Bearings: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.overallBearingCondRating,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.14 NSTM Inspection Condition: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, nstmInspectionCondition),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "B.C.08 Joints: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.overallJointCondRating,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "B.C.15 Underwater: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.underwaterRating,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "IE.36 Inspection Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspection?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Appraisal Ratings",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.C.05 Railing: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.railRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "B.AP.01 Approach Alignment: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.BRIDGE_CONDITION_RATING,
              structure?.inspection?.apprAlign,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.C.06 Transition: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.transRating),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "AP.02 Approach Slab: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.apprSlabCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.N.06 Substructure Nav. Protection: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.PIER_PROTECTION, structure?.inspection?.pierprot),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "AP.01 Approach Roadway: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.apprRoadCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.AP.03 Scour Vulnerability: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_VULNERABILITY,
              structure?.userInspection?.scourVulnerability,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "DK.01 Deck Wearing Surface: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.deckWsCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "SUP.02 New Protective Coating? ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: inspectionStore.getReportCheckbox(structure?.userInspection?.newPaint),
            style: "customFontAwesome",
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "WA.01 SCBI: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_CRITICAL_BRIDGE_INDICATOR,
              structure?.inspection?.scourCritical,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "SUP.03 Protective Coating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING,
              structure?.userInspection?.paintCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "WA.02 SCBI Category: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.inspection?.scourCriticalCat,
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "SUP.04 Protective Coating (Extent): ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING_EXTENT,
              structure?.userInspection?.paintExtentRate,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "B.C.12 Bridge Condition: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.BRIDGE_CONDITION_RATING,
              structure?.inspection?.condition,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.AP.05 Seismic Vulnerability:",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.SEISMIC_VULNERABILITY, seismicVulnerability),
            margin: [0, 5, 0, 0],
          },
          {
            width: "25%",
            text: "M.28 Deficiency Score: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure.userInspection.maintDefRate,
            margin: [0, 5, 0, 0],
          },
        ],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}
function getNSTMInspectionCondition(structure) {
  if (structure?.userBridge?.mainFCGroupNum < 5 || structure?.userBridge?.apprFCGroupNum < 5) {
    return structure?.inspection?.supRating;
  } else {
    return "N";
  }
}
function getCondition(inspectionStore, nstmInspectionCondition) {
  if (!nstmInspectionCondition && !inspectionStore.getNstmInspectionConditionChangedByUser) {
    return getNSTMInspectionCondition();
  } else {
    return nstmInspectionCondition;
  }
}
function getSeismicVulnerability(inspectionStore, seismicVulnerability) {
  if (!seismicVulnerability && !inspectionStore.getSeismicVulnerabilityChangedByUser) {
    return "0";
  } else {
    return seismicVulnerability;
  }
}
function checkPrevAcceptedInspection(value) {
  if (value) {
    return value;
  } else {
    return "--";
  }
}
function getCheckboxChangedAsterisk(currentRequired, previousRequired, currentInterval, previousInterval) {
  let needsRequiredAsterisk = currentRequired != previousRequired;
  let needsIntervalAsterisk = currentInterval != previousInterval;
  let needsAsterisk = false;
  if (!needsRequiredAsterisk || !needsIntervalAsterisk) {
    needsAsterisk =
      currentInterval == "0 mos" && previousInterval == "--" ? false : currentInterval != previousInterval;
  }

  return needsAsterisk ? "*" : "";
}
function getRatingsTable(inspectionStore, structure, getFormattedDateStringNoTime, inspectionFields) {
  const fieldsToCompare = [
    {
      rowName: "IE.05 Inspection Date:",
      currentCol: getFormattedDateStringNoTime(structure.inspection.inspectionDate),
      previousCol: getFormattedDateStringNoTime(inspectionFields?.inspectionDate),
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "IE.08 Inspection Type:",
      currentCol: structure.inspection.inspectionType || "--",
      previousCol: inspectionFields?.inspectionType || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.01 Deck:",
      currentCol: structure.inspection.dkRating || "--",
      previousCol: inspectionFields?.dkRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.02 Superstructure:",
      currentCol: structure.inspection.supRating || "--",
      previousCol: inspectionFields?.supRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.03 Substructure:",
      currentCol: structure.inspection.subRating || "--",
      previousCol: inspectionFields?.subRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.04 Culvert:",
      currentCol: structure.inspection.culvRating || "--",
      previousCol: inspectionFields?.culvRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.05 Railings:",
      currentCol: structure.inspection.railRating || "--",
      previousCol: inspectionFields?.railRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.06 Transition:",
      currentCol: structure.inspection.transRating || "--",
      previousCol: inspectionFields?.transRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.07 Bearings:",
      currentCol: structure.userInspection?.overallBearingCondRating || "--",
      previousCol: inspectionFields?.bearingConditionRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.08 Joints:",
      currentCol: structure.userInspection?.overallJointCondRating || "--",
      previousCol: inspectionFields?.jointConditionRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.09 Channel:",
      currentCol: structure.inspection.chanRating || "--",
      previousCol: inspectionFields?.chanRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.10 Channel Protection:",
      currentCol: structure.userInspection.chanProtRating || "--",
      previousCol: inspectionFields?.chanProtRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.11 Scour:",
      currentCol: inspectionStore.getOverallScourHole(structure?.userInspection?.scourCondRating, null, structure),
      previousCol: checkPrevAcceptedInspection(
        inspectionStore.getOverallScourHole(inspectionFields?.scourCondRating, inspectionFields?.scourHole, null),
      ),
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },

    {
      rowName: "B.C.14 NSTM:",
      currentCol: structure.userInspection.nstmCondition || "--",
      previousCol: inspectionFields?.nstmCondition || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "B.C.15 Underwater:",
      currentCol: structure.userInspection.underwaterRating || "--",
      previousCol: inspectionFields?.underwaterRating || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "WA.01 SCBI:",
      currentCol: structure.inspection.scourCritical || "--",
      previousCol: inspectionFields?.scourCritical || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "WA.02 SCBI Category:",
      currentCol: structure.inspection.scourCriticalCat || "--",
      previousCol: inspectionFields?.scourCriticalCat || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
    {
      rowName: "WA.43 Streambed Material:",
      currentCol: getIN15Value(structure.structureUnits, structure.inspectionsUnderwater),
      previousCol: inspectionFields?.streamBedMaterials
        ? getIN15ValueFromMaterials(inspectionFields.streamBedMaterials)
        : "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
      nonControllingChange: areThereNonControllingIN15Changes(
        structure.structureUnits,
        structure.inspectionsUnderwater,
        inspectionFields?.streamBedMaterials,
      )
        ? "*"
        : "",
    },
    {
      rowName: "B.C.12 Bridge Condition:",
      currentCol: structure.inspection.condition || "--",
      previousCol: inspectionFields?.condition || "--",
      get differing() {
        return this.currentCol == this.previousCol ? "" : " *";
      },
    },
  ];

  let finalTable = [];
  for (const field of fieldsToCompare) {
    let row = [
      {
        text: field.rowName + field.differing,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: field?.nonControllingChange ? field.currentCol + field?.nonControllingChange : field.currentCol,
        alignment: "center",
        margin: [0, 5, 0, 0],
      },
      {
        text: field.previousCol,
        alignment: "center",
        margin: [0, 5, 0, 0],
      },
    ];
    finalTable.push(row);
  }

  return {
    table: {
      headerRows: 0,
      widths: ["54%", "24%", "22%"],
      body: finalTable,
    },
    layout: "noBorders",
    margin: [0, 0, 0, 0],
  };
}
export { setRatingsDocDefinition };
